import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// Timing (ms)
const WORD_DELAY   = 700;   // gap between each word appearing on first load
const CYCLE_PAUSE  = 1800;  // how long each cycling word holds
const FADE_MS      = 250;   // fade between cycling words
const REPEAT_PAUSE = 9000;  // how long to rest on "Book." before cycling again

const CYCLE = ['Listen.', 'Enjoy.', 'Book.'];

export default function IrishJazzLogo() {
  const [showExplore,  setShowExplore]  = useState(false);
  const [showDiscover, setShowDiscover] = useState(false);
  const [cycleStep,    setCycleStep]    = useState(-1);
  const [wordVisible,  setWordVisible]  = useState(true);

  // Initial sequential word reveal
  useEffect(() => {
    const t1 = setTimeout(() => setShowExplore(true),  WORD_DELAY);
    const t2 = setTimeout(() => setShowDiscover(true), WORD_DELAY * 2);
    const t3 = setTimeout(() => setCycleStep(0),       WORD_DELAY * 3);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  // Cycle: advance through words, then rest on Book and repeat
  useEffect(() => {
    if (cycleStep < 0) return;

    const isLast = cycleStep === CYCLE.length - 1;
    const holdTime = isLast ? REPEAT_PAUSE : CYCLE_PAUSE;

    const hold = setTimeout(() => {
      setWordVisible(false);
      setTimeout(() => {
        setCycleStep(isLast ? 0 : cycleStep + 1);
        setWordVisible(true);
      }, FADE_MS);
    }, holdTime);

    return () => clearTimeout(hold);
  }, [cycleStep]);

  const wordOpacity = (show: boolean) => ({
    opacity: show ? 1 : 0,
    transition: `opacity ${FADE_MS}ms ease`,
  });

  return (
    <Box sx={{ flexShrink: 0, pr: { md: 6 } }}>
      <Typography sx={{
        fontSize: '0.75rem',
        letterSpacing: '0.3em',
        color: 'text.disabled',
        textTransform: 'uppercase',
        display: 'block',
        mb: 0.5,
      }}>
        This is
      </Typography>

      <Typography component="span" sx={{
        fontSize: { xs: '4.5rem', sm: '5.5rem', md: '6.5rem' },
        fontWeight: 900,
        letterSpacing: '-0.02em',
        lineHeight: 0.86,
        color: 'text.primary',
        textTransform: 'uppercase',
        display: 'block',
      }}>
        Irish
      </Typography>

      <Typography component="span" sx={{
        fontSize: { xs: '4.5rem', sm: '5.5rem', md: '6.5rem' },
        fontWeight: 900,
        letterSpacing: '-0.02em',
        lineHeight: 0.86,
        color: '#C9A35A',
        textTransform: 'uppercase',
        display: 'block',
      }}>
        Jazz
      </Typography>

      {/* Animated tagline */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '0.35em', mt: 1.25, minHeight: '1.4em' }}>
        <Typography sx={{
          fontSize: '0.90rem',
          letterSpacing: '0.12em',
          color: 'text.disabled',
          textTransform: 'uppercase',
          ...wordOpacity(showExplore),
        }}>
          Explore.
        </Typography>

        <Typography sx={{
          fontSize: '0.90rem',
          letterSpacing: '0.12em',
          color: 'primary.main',
          fontWeight: 700,
          textTransform: 'uppercase',
          ...wordOpacity(showDiscover),
        }}>
          Discover.
        </Typography>

        {cycleStep >= 0 && (
          <Typography sx={{
            fontSize: '0.90rem',
            letterSpacing: '0.12em',
            color: 'text.disabled',
            textTransform: 'uppercase',
            opacity: wordVisible ? 1 : 0,
            transition: `opacity ${FADE_MS}ms ease`,
          }}>
            {CYCLE[cycleStep]}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
